import { useEffect, useState } from "react";
import { view } from "./OncoalertView";
import { useSelector, useDispatch } from "react-redux";
import {
  logAnalyticEvent,
  getUrlParams,
  getISOFormattedDateTimeString,
} from "../../utils";
import useDebounce from "../../utils/useDebounce";
import { useGetTweets } from "../../store/queries/tweets";
import {
  useGetPaginatedTopConferences,
  useTopConferences,
} from "../../store/queries/tags";
import moment from "moment";
import { MAX_RESULTS } from "../../utils/variables";
import { settingsActions } from "../../store/actions";

export const OncoalertInfluencers = () => {
  const dispatch = useDispatch();
  const queryParams = getUrlParams(location.search);

  const auth = useSelector((state) => state.auth.data);
  const oncoalertSettings = useSelector((state) => state.settings?.oncoalert);

  const [selectedConference, setSelectedConference] = useState(null);
  const [hashtagTerm, setHashtagTerm] = useState("");
  const [showSignupModal, setShowSignupModal] = useState(false);

  const debouncedHashtagTerm = useDebounce(hashtagTerm, 500);

  const {
    data: hashtags,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useGetPaginatedTopConferences(
    {
      term: debouncedHashtagTerm,
      maxResult: MAX_RESULTS,
    },
    {
      getNextPageParam: (lastPage, pages) => {
        return lastPage.data.length === MAX_RESULTS ? pages.length : undefined;
      },
    }
  );

  const {
    data: conferences,
    isLoading: loadingConferences,
    isSuccess: successConferences,
  } = useTopConferences({
    maxResult: 15,
  });

  const handleMoreHashes = () => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  };

  const { data: tweets, isLoading: isLoadingTweets } = useGetTweets(
    {
      ...(selectedConference && { addTagIds: selectedConference }),
      ...(auth?.id && { userId: auth.id }),
      maxResult: 1,
      pageNum: 0,
      sortBy: "score",
      sortOrder: "desc",
      originalOnly: false,
      includeReplies: true,
      startDate: getISOFormattedDateTimeString(
        moment(conferences?.data?.[0]?.monitorStartTime)?.utc()?._d
      ),
      endDate: getISOFormattedDateTimeString(
        moment(conferences?.data?.[0]?.monitorEndTime)?.utc()?._d
      ),
    },
    { enabled: !!selectedConference }
  );

  useEffect(() => {
    logAnalyticEvent(
      "OncoAlert Hashtags",
      {
        page: "Conference hashtags list",
      },
      auth?.id || queryParams?.u || null,
      auth?.displayName || queryParams?.n || null,
      auth?.email || queryParams?.e || null,
      auth?.createdAt || null,
      auth?.preferences?.filter((p) => p.preferenceName === "UserType")?.[0]
        ?.preferenceStringValue ||
        queryParams?.t ||
        "MEDICAL",
      null
    );
  }, []);

  useEffect(() => {
    if (conferences?.data?.length > 0) {
      setSelectedConference([conferences.data[0].tag.id]);
    }
  }, [conferences?.data]);

  const handleAddSelectedHashtag = (e) => {
    const tag = e?.tag?.name?.replace("#", "");
    window.location.href = `/oncoalert/hashtag/${tag}`;
  };

  const handleSignUpClick = () => {
    setShowSignupModal(true);
  };

  const handlePreviousPage = () => {
    const currentPage = window.location.pathname;
    dispatch(
      settingsActions.set({
        oncoalert: { ...oncoalertSettings, previousPage: currentPage },
      })
    );
  };

  return view({
    handleSignUpClick,
    tweet: tweets?.data?.[0],
    isLoadingTweets,

    conferences: conferences?.data,
    loadingConferences,
    successConferences,

    hashtags: hashtags?.pages,
    hashtagsInfiniteScroll: {
      loading: isFetchingNextPage,
      hasNextPage,
      onLoadMore: handleMoreHashes,
    },
    hashtagTerm,
    setHashtagTerm,
    addSelectedHashtag: handleAddSelectedHashtag,
    showSignupModal,
    setShowSignupModal,
    handlePreviousPage,
  });
};
